import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@fluentui/react-components";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  setProviderDates,
  setCurrentStep,
  fetchStatements,
  fetchStatementDetail,
  setColumnMapping,
  setParsedHeaders,
  setResumedStatementMeta,
} from "../../../redux/Commission/commissionStatementSlice";
import apiServices from "../../../service";
import SessionResumePanel from "./SessionResumePanel";
import SessionsModal from "./SessionsModal";

const PROVIDER_PAGE_LIMIT = 3000;

const NAVY = "#205393";
const T1 = "#0f172a";
const T2 = "#475569";
const RED = "#c92a2a";
const ACC = "#1e4a8a";
const ACCL = "#e8f0fb";
const BRD = "#e2e5ea";
const BRD2 = "#c8cdd5";
const RANGE_BG = "#dbeafe";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const Label: React.FC<{ children: React.ReactNode; required?: boolean; htmlFor?: string }> = ({ children, required, htmlFor }) => (
  <label htmlFor={htmlFor} style={{ fontSize: "11px", fontWeight: 600, color: T2, letterSpacing: "0.04em", marginBottom: "3px", display: "block" }}>
    {children} {required && <span style={{ color: RED }}>*</span>}
  </label>
);

const fieldStyle: React.CSSProperties = {
  padding: "5px 9px",
  border: `1px solid ${BRD2}`,
  borderRadius: "5px",
  fontSize: "12.5px",
  background: "#fff",
  color: T1,
  height: "30px",
  outline: "none",
};

// ── Utilities ──────────────────────────────────────────────────────────────────

const toISO = (y: number, m: number, d: number) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const getDIM = (y: number, m: number) => new Date(y, m + 1, 0).getDate();

function buildGrid(year: number, month: number) {
  const fd = new Date(year, month, 1).getDay();
  const dim = getDIM(year, month);
  const prevY = month === 0 ? year - 1 : year;
  const prevM = month === 0 ? 11 : month - 1;
  const nextY = month === 11 ? year + 1 : year;
  const nextM = month === 11 ? 0 : month + 1;
  const prevDim = getDIM(prevY, prevM);

  const cells: { iso: string; day: number; current: boolean }[] = [];
  for (let i = fd - 1; i >= 0; i--)
    cells.push({ iso: toISO(prevY, prevM, prevDim - i), day: prevDim - i, current: false });
  for (let d = 1; d <= dim; d++)
    cells.push({ iso: toISO(year, month, d), day: d, current: true });
  for (let d = 1; cells.length < 42; d++)
    cells.push({ iso: toISO(nextY, nextM, d), day: d, current: false });

  const rows: (typeof cells)[] = [];
  for (let i = 0; i < 42; i += 7) rows.push(cells.slice(i, i + 7));
  return rows;
}

// ── DateRangePicker ────────────────────────────────────────────────────────────

interface DRPProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (v: string) => void;
  onEndDateChange: (v: string) => void;
}

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const DateRangePicker: React.FC<DRPProps> = ({ startDate, endDate, onStartDateChange, onEndDateChange }) => {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState<string | null>(null);
  const [picking, setPicking] = useState<"start" | "end">("start");
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [open]);

  const openCalendar = () => {
    const base = startDate ? new Date(startDate + "T00:00:00") : new Date();
    setViewYear(base.getFullYear());
    setViewMonth(base.getMonth());
    setPicking("start");
    setHover(null);
    setOpen(true);
  };

  const rMonth = viewMonth === 11 ? 0 : viewMonth + 1;
  const rYear = viewMonth === 11 ? viewYear + 1 : viewYear;

  const prevM = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextM = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const handleDayClick = (iso: string, current: boolean) => {
    if (!current) return;
    if (picking === "start") {
      onStartDateChange(iso);
      onEndDateChange("");
      setPicking("end");
    } else {
      if (startDate && iso < startDate) {
        onStartDateChange(iso);
        onEndDateChange(startDate);
      } else {
        onEndDateChange(iso);
      }
      setOpen(false);
      setPicking("start");
      setHover(null);
    }
  };

  // Compute effective range (includes hover preview)
  const effEnd = endDate || (picking === "end" && hover ? hover : null);
  const lo = startDate && effEnd ? (startDate <= effEnd ? startDate : effEnd) : startDate || null;
  const hi = startDate && effEnd ? (startDate <= effEnd ? effEnd : startDate) : null;

  const getCellBg = (iso: string) => {
    if (!lo) return "transparent";
    if (!hi || lo === hi) return "transparent";
    if (iso === lo) return `linear-gradient(to right, transparent 50%, ${RANGE_BG} 50%)`;
    if (iso === hi) return `linear-gradient(to left, transparent 50%, ${RANGE_BG} 50%)`;
    if (iso > lo && iso < hi) return RANGE_BG;
    return "transparent";
  };

  const isCircle = (iso: string) =>
    (iso === startDate && !!startDate) || (iso === endDate && !!endDate);

  const renderMonth = (year: number, month: number) => {
    const rows = buildGrid(year, month);
    return (
      <div style={{ width: "210px" }}>
        <div style={{ textAlign: "center", fontWeight: 700, fontSize: "13px", color: T1, marginBottom: "10px" }}>
          {MONTH_NAMES[month]} {year}
        </div>
        <table role="grid" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: "100%" }}>
          <thead>
            <tr>
              {DAY_NAMES.map(d => (
                <th key={d} style={{
                  textAlign: "center", fontSize: "11px", fontWeight: 700,
                  color: NAVY, padding: "2px 0", width: "30px",
                }}>{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map(({ iso, day, current }) => {
                  const circle = isCircle(iso);
                  const bg = getCellBg(iso);
                  const [isoY, isoM, isoD] = iso.split("-").map(Number);
                  return (
                    <td
                      key={iso}
                      role="gridcell"
                      tabIndex={current ? 0 : -1}
                      aria-label={`${MONTH_NAMES[isoM - 1]} ${isoD}, ${isoY}`}
                      aria-selected={circle}
                      onClick={() => handleDayClick(iso, current)}
                      onKeyDown={(e) => { if (current && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); handleDayClick(iso, current); } }}
                      onMouseEnter={() => current && picking === "end" && setHover(iso)}
                      onMouseLeave={() => setHover(null)}
                      style={{
                        background: bg,
                        padding: "1px 0",
                        cursor: current ? "pointer" : "default",
                      }}
                    >
                      <div style={{
                        width: "28px",
                        height: "28px",
                        lineHeight: "28px",
                        borderRadius: "50%",
                        textAlign: "center",
                        margin: "0 auto",
                        background: circle ? NAVY : "transparent",
                        color: circle ? "#fff" : current ? T1 : "#c0c7d1",
                        fontSize: "12.5px",
                        fontWeight: circle ? 700 : 400,
                        userSelect: "none",
                        transition: "background 0.1s",
                      }}>
                        {day}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Manual inputs + calendar toggle */}
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <input
          id="start-date-input"
          type="date"
          value={startDate}
          onChange={(e) => {
            onStartDateChange(e.target.value);
            if (endDate && e.target.value > endDate) onEndDateChange("");
          }}
          style={{ ...fieldStyle, flex: 1, minWidth: 0, boxSizing: "border-box" }}
        />
        <span style={{ color: "#94a3b8", fontSize: "12px", flexShrink: 0 }}>–</span>
        <input
          id="end-date-input"
          type="date"
          value={endDate}
          min={startDate || undefined}
          onChange={(e) => onEndDateChange(e.target.value)}
          style={{ ...fieldStyle, flex: 1, minWidth: 0, boxSizing: "border-box" }}
        />
        <button
          onClick={openCalendar}
          title="Open calendar picker"
          style={{
            height: "30px", width: "30px", flexShrink: 0,
            border: `1px solid ${BRD2}`, borderRadius: "5px",
            background: open ? NAVY : "#fff",
            color: open ? "#fff" : "#475569",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <CalendarIcon />
        </button>
      </div>

      {/* Dropdown calendar */}
      {open && (
        <div role="dialog" aria-label="Date picker" style={{
          position: "absolute",
          top: "calc(100% + 5px)",
          left: 0,
          zIndex: 400,
          background: "#fff",
          border: `1px solid ${BRD2}`,
          borderRadius: "8px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.14)",
          padding: "14px 8px 12px",
          display: "inline-flex",
          flexDirection: "column",
        }}>
          {/* Calendars row */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: "0" }}>
            {/* Prev arrow */}
            <button onClick={prevM} style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: "20px", color: T2, padding: "2px 6px",
              lineHeight: 1, marginTop: "1px", flexShrink: 0,
            }}>‹</button>

            {/* Left month */}
            {renderMonth(viewYear, viewMonth)}

            {/* Divider */}
            <div style={{ width: "1px", background: BRD, margin: "0 10px", alignSelf: "stretch" }} />

            {/* Right month */}
            {renderMonth(rYear, rMonth)}

            {/* Next arrow */}
            <button onClick={nextM} style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: "20px", color: T2, padding: "2px 6px",
              lineHeight: 1, marginTop: "1px", flexShrink: 0,
            }}>›</button>
          </div>

          {/* Footer */}
          <div style={{
            marginTop: "10px",
            paddingTop: "10px",
            borderTop: `1px solid ${BRD}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingLeft: "6px",
            paddingRight: "6px",
          }}>
            <span style={{ fontSize: "12px", color: T2 }}>
              {picking === "start" ? "Select start date" : "Now select end date"}
            </span>
            <button
              onClick={() => { onStartDateChange(""); onEndDateChange(""); setPicking("start"); }}
              style={{ background: "none", border: "none", cursor: "pointer", color: RED, fontSize: "12px", fontWeight: 500, padding: "0 4px" }}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── StepProviderDates ──────────────────────────────────────────────────────────

const StepProviderDates: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { providerId, providerName, startDate, endDate, receiptDate, selectedStatementId, statements, loading, loadingDetail } =
    useSelector((s: RootState) => s.commissionStatement);

  const [providers, setProviders] = useState<{ id: string; name: string }[]>([]);
  const [localProviderId, setLocalProviderId] = useState(providerId);
  const [localProviderName, setLocalProviderName] = useState(providerName);
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const [localReceiptDate, setLocalReceiptDate] = useState(receiptDate);
  const [dateError, setDateError] = useState("");
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [providerError, setProviderError] = useState("");
  const [statementsModalOpen, setStatementsModalOpen] = useState(false);
  const [providerSearch, setProviderSearch] = useState(providerName);
  const [providerOpen, setProviderOpen] = useState(false);
  const [providerFocusIdx, setProviderFocusIdx] = useState(-1);
  const providerRef = useRef<HTMLDivElement>(null);

  const filteredProviders = useMemo(
    () => providers.filter(p => p.name.toLowerCase().includes(providerSearch.toLowerCase())),
    [providers, providerSearch],
  );

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (providerRef.current && !providerRef.current.contains(e.target as Node)) {
        setProviderOpen(false);
        setProviderSearch(localProviderName);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [localProviderName]);

  const loadProviders = () => {
    setLoadingProviders(true);
    setProviderError("");
    apiServices.systemManager.providers
      .getList(1, PROVIDER_PAGE_LIMIT)
      .then((res: any) => {
        const items = (res.data?.data || []).map((p: any) => ({
          id: p.ProviderId || p.id,
          name: p.ProviderName || p.provider_name || p.name,
        }));
        setProviders(items);
      })
      .catch(() => setProviderError("Failed to load providers. Please try again."))
      .finally(() => setLoadingProviders(false));
  };

  useEffect(() => { loadProviders(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (localProviderId && localStartDate && localEndDate) {
      dispatch(fetchStatements({ providerId: localProviderId, startDate: localStartDate, endDate: localEndDate }));
    }
  }, [localProviderId, localStartDate, localEndDate, dispatch]);

  const validate = () => {
    if (!localProviderId || !localStartDate || !localEndDate || !localReceiptDate) {
      setDateError("All fields are required.");
      return false;
    }
    if (localStartDate > localEndDate) {
      setDateError("Start date must not be after end date.");
      return false;
    }
    setDateError("");
    return true;
  };

  const handleNext = () => {
    if (!validate()) return;
    dispatch(
      setProviderDates({
        providerId: localProviderId,
        providerName: localProviderName,
        startDate: localStartDate,
        endDate: localEndDate,
        receiptDate: localReceiptDate,
      })
    );
    if (selectedStatementId) {
      const stmt = statements.find((s) => s.CommissionStatementId === selectedStatementId);

      if ((stmt?.TotalRows ?? 0) > 0) {
        // Detail records exist → go to Step 3 (Validate & Match)
        if (stmt?.ColumnMapping) {
          dispatch(setColumnMapping(stmt.ColumnMapping));
        }
        dispatch(fetchStatementDetail(selectedStatementId));
      } else {
        // No detail records yet → go to Step 2 (Column Mapping)
        dispatch(setResumedStatementMeta({
          providerId: localProviderId,
          providerName: localProviderName,
          startDate: localStartDate,
          endDate: localEndDate,
          receiptDate: localReceiptDate,
          fileName: stmt?.FileName ?? null,
          statementId: selectedStatementId,
          columnMapping: null, // null prevents setResumedStatementMeta from overwriting parsedHeaders with mapping values
        }));
        dispatch(setParsedHeaders(stmt?.Headers ?? []));
        if (stmt?.ColumnMapping) {
          dispatch(setColumnMapping(stmt.ColumnMapping));
        }
        dispatch(setCurrentStep(2));
      }
    } else {
      dispatch(setCurrentStep(1));
    }
  };

  const canProceed = !!localProviderId && !!localStartDate && !!localEndDate && !!localReceiptDate;
  const isResuming = loadingDetail;
  const nextLabel = selectedStatementId ? "Resume Upload →" : "Next →";

  return (
    <div>
      <div style={{ background: "#fff", border: `1px solid ${BRD}`, borderRadius: "5px", padding: "14px 16px", marginBottom: "12px" }}>
        <div style={{ fontSize: "13px", fontWeight: 600, color: T1, marginBottom: "12px" }}>
          Step 1 — Select Provider, Date Range &amp; Receipt Date
        </div>

        {/* Filter bar — 3 columns: provider | date range (wider) | receipt date */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: "12px", marginBottom: "14px" }}>

          {/* Provider */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Label required htmlFor="provider-search">Provider</Label>
            {loadingProviders ? (
              <Spinner size="tiny" />
            ) : providerError ? (
              <div style={{ fontSize: "12px", color: RED, display: "flex", alignItems: "center", gap: "6px", marginTop: "4px" }}>
                <span>{providerError}</span>
                <button
                  onClick={loadProviders}
                  style={{ background: "none", border: "none", color: RED, cursor: "pointer", fontSize: "12px", fontWeight: 600, padding: 0, textDecoration: "underline" }}
                >
                  Retry
                </button>
              </div>
            ) : (
              <div ref={providerRef} style={{ position: "relative" }}>
                <div style={{ position: "relative" }}>
                  <input
                    id="provider-search"
                    type="text"
                    role="combobox"
                    aria-expanded={providerOpen}
                    aria-haspopup="listbox"
                    aria-autocomplete="list"
                    aria-controls="provider-listbox"
                    aria-activedescendant={providerFocusIdx >= 0 ? `provider-opt-${filteredProviders[providerFocusIdx]?.id}` : undefined}
                    placeholder="Select provider"
                    value={providerSearch}
                    onFocus={() => setProviderOpen(true)}
                    onChange={(e) => {
                      setProviderSearch(e.target.value);
                      setProviderOpen(true);
                      setProviderFocusIdx(-1);
                      if (!e.target.value) {
                        setLocalProviderId("");
                        setLocalProviderName("");
                      }
                    }}
                    onKeyDown={(e) => {
                      if (!providerOpen) {
                        if (e.key === "ArrowDown" || e.key === "Enter") setProviderOpen(true);
                        return;
                      }
                      if (e.key === "Escape") {
                        setProviderOpen(false);
                        setProviderSearch(localProviderName);
                        setProviderFocusIdx(-1);
                      } else if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setProviderFocusIdx(i => Math.min(i + 1, filteredProviders.length - 1));
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setProviderFocusIdx(i => Math.max(i - 1, 0));
                      } else if (e.key === "Enter" && providerFocusIdx >= 0) {
                        e.preventDefault();
                        const p = filteredProviders[providerFocusIdx];
                        setLocalProviderId(p.id);
                        setLocalProviderName(p.name);
                        setProviderSearch(p.name);
                        setProviderOpen(false);
                        setProviderFocusIdx(-1);
                      } else if (e.key === "Tab") {
                        setProviderOpen(false);
                        setProviderFocusIdx(-1);
                      }
                    }}
                    style={{ ...fieldStyle, width: "100%", boxSizing: "border-box", paddingRight: "28px" }}
                  />
                  <span style={{
                    position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)",
                    color: "#94a3b8", fontSize: "11px", pointerEvents: "none",
                  }}>▼</span>
                </div>
                {providerOpen && (
                  <div
                    id="provider-listbox"
                    role="listbox"
                    style={{
                      position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0,
                      background: "#fff", border: "1px solid #c8cdd5", borderRadius: "5px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 100,
                      maxHeight: "220px", overflowY: "auto",
                    }}
                  >
                    {filteredProviders.length === 0 ? (
                      <div style={{ padding: "10px 12px", fontSize: "12.5px", color: "#94a3b8" }}>No providers found</div>
                    ) : (
                      filteredProviders.map((p, idx) => (
                          <div
                            key={p.id}
                            id={`provider-opt-${p.id}`}
                            role="option"
                            aria-selected={p.id === localProviderId}
                            onMouseDown={() => {
                              setLocalProviderId(p.id);
                              setLocalProviderName(p.name);
                              setProviderSearch(p.name);
                              setProviderOpen(false);
                              setProviderFocusIdx(-1);
                            }}
                            style={{
                              padding: "8px 12px", fontSize: "12.5px", cursor: "pointer",
                              background: p.id === localProviderId ? "#e8f0fb" : idx === providerFocusIdx ? "#f0f4ff" : "#fff",
                              color: p.id === localProviderId ? "#205393" : "#0f172a",
                              fontWeight: p.id === localProviderId ? 600 : 400,
                            }}
                            onMouseEnter={e => { if (p.id !== localProviderId) (e.currentTarget as HTMLDivElement).style.background = "#f8fafc"; }}
                            onMouseLeave={e => { if (p.id !== localProviderId) (e.currentTarget as HTMLDivElement).style.background = idx === providerFocusIdx ? "#f0f4ff" : "#fff"; }}
                          >
                            {p.name}
                          </div>
                        ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Date Range — dual-month calendar picker */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Label required htmlFor="start-date-input">Date Range</Label>
            <DateRangePicker
              startDate={localStartDate}
              endDate={localEndDate}
              onStartDateChange={setLocalStartDate}
              onEndDateChange={setLocalEndDate}
            />
          </div>

          {/* Receipt Date */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Label required htmlFor="receipt-date">Receipt Date</Label>
            <input
              id="receipt-date"
              type="date"
              value={localReceiptDate}
              onChange={(e) => setLocalReceiptDate(e.target.value)}
              style={{ ...fieldStyle, width: "100%", boxSizing: "border-box" }}
            />
          </div>
        </div>

        {/* Statement resume panel */}
        {localProviderId && localStartDate && localEndDate && (
          <SessionResumePanel onOpenAll={() => setStatementsModalOpen(true)} />
        )}

        {dateError && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12.5px", color: RED, marginTop: "8px" }}>
            <span style={{ flex: 1 }}>{dateError}</span>
            <button
              onClick={() => setDateError("")}
              style={{ background: "none", border: "none", color: RED, cursor: "pointer", fontSize: "14px", lineHeight: 1, padding: "0 2px", flexShrink: 0 }}
              aria-label="Dismiss"
            >×</button>
          </div>
        )}

        {/* Button row */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "14px", paddingTop: "12px", borderTop: `1px solid ${BRD}` }}>
          <div style={{ flex: 1 }} />
          <button
            onClick={handleNext}
            disabled={!canProceed || isResuming}
            style={{
              background: canProceed && !isResuming ? NAVY : "#c8cdd5",
              color: "#fff",
              border: `1px solid ${canProceed && !isResuming ? NAVY : "#c8cdd5"}`,
              borderRadius: "5px",
              fontSize: "12.5px",
              fontWeight: 500,
              padding: "0 16px",
              height: "32px",
              cursor: canProceed && !isResuming ? "pointer" : "not-allowed",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {isResuming ? <Spinner size="tiny" /> : null}
            {isResuming ? "Loading…" : nextLabel}
          </button>
        </div>
      </div>

      {/* Info box */}
      <div style={{ background: ACCL, border: "1px solid #bcd1f5", borderRadius: "5px", padding: "10px 14px", fontSize: "12.5px", color: ACC }}>
        <strong>How it works</strong>
        <ul style={{ paddingLeft: "16px", marginTop: "4px", lineHeight: 1.9, color: T2 }}>
          <li>Select Provider, Date Range, and the Receipt Date to be applied to all receipts in this upload.</li>
          <li>Upload your commission statement, map columns, then select rows to process.</li>
          <li>You can process receipts in stages — come back later and the system will show your progress.</li>
        </ul>
      </div>

      <SessionsModal open={statementsModalOpen} onClose={() => setStatementsModalOpen(false)} />
    </div>
  );
};

export default StepProviderDates;
